/**
 * ReviewModal Component
 * 
 * Clean review interface for Facts and Aliases with tabs.
 */

import React, { useState, useEffect } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { X, Check, Loader, Sparkles, AlertCircle, Link2 } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import { getPendingFacts, confirmFact, rejectFact, editFact } from '../../utils/memoryApi';
import { getReviewQueue, reviewEntityItem } from '../../utils/entityApi';
import FactCard from './FactCard';
import AliasCard from './AliasCard';

export default function ReviewModal({ isOpen, onClose, onReviewChanged }) {
  const { getToken } = useKindeAuth();
  
  const [activeTab, setActiveTab] = useState('facts');
  
  // Facts state
  const [factsLoading, setFactsLoading] = useState(true);
  const [factsError, setFactsError] = useState(null);
  const [facts, setFacts] = useState([]);
  const [factsProcessingId, setFactsProcessingId] = useState(null);
  const [editingFact, setEditingFact] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [confirmingAll, setConfirmingAll] = useState(false);
  
  // Aliases state
  const [aliasesLoading, setAliasesLoading] = useState(true);
  const [aliasesError, setAliasesError] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [aliasesProcessingId, setAliasesProcessingId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadFacts();
      loadAliases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ============ FACTS LOGIC ============

  const loadFacts = async () => {
    setFactsLoading(true);
    setFactsError(null);
    try {
      const token = await getToken();
      const data = await getPendingFacts(token, 20);
      setFacts(data.facts || []);
    } catch (err) {
      console.error('Failed to load pending facts:', err);
      setFactsError('Failed to load facts.');
    } finally {
      setFactsLoading(false);
    }
  };

  const handleConfirmFact = async (factId) => {
    setFactsProcessingId(factId);
    try {
      const token = await getToken();
      await confirmFact(factId, token);
      setFacts(prev => prev.filter(f => f.id !== factId));
      onReviewChanged?.();
    } catch (err) {
      console.error('Failed to confirm fact:', err);
      setFactsError('Failed to confirm fact.');
    } finally {
      setFactsProcessingId(null);
    }
  };

  const handleConfirmAllFacts = async () => {
    if (facts.length === 0) return;
    setConfirmingAll(true);
    setFactsError(null);
    try {
      const token = await getToken();
      for (const fact of facts) {
        await confirmFact(fact.id, token);
      }
      setFacts([]);
      onReviewChanged?.();
    } catch (err) {
      console.error('Failed to confirm all facts:', err);
      setFactsError('Failed to confirm some facts.');
      loadFacts();
    } finally {
      setConfirmingAll(false);
    }
  };

  const handleRejectFact = async (factId) => {
    setFactsProcessingId(factId);
    try {
      const token = await getToken();
      await rejectFact(factId, token);
      setFacts(prev => prev.filter(f => f.id !== factId));
      onReviewChanged?.();
    } catch (err) {
      console.error('Failed to reject fact:', err);
      setFactsError('Failed to reject fact.');
    } finally {
      setFactsProcessingId(null);
    }
  };

  const handleStartEditFact = (fact) => {
    setEditingFact(fact);
    setEditContent(fact.content);
  };

  const handleSaveEditFact = async () => {
    if (!editingFact || !editContent.trim()) return;
    setFactsProcessingId(editingFact.id);
    try {
      const token = await getToken();
      await editFact(editingFact.id, { content: editContent.trim() }, token);
      setFacts(prev => prev.map(f => 
        f.id === editingFact.id ? { ...f, content: editContent.trim() } : f
      ));
      setEditingFact(null);
      setEditContent('');
    } catch (err) {
      console.error('Failed to edit fact:', err);
      setFactsError('Failed to save changes.');
    } finally {
      setFactsProcessingId(null);
    }
  };

  const handleCancelEditFact = () => {
    setEditingFact(null);
    setEditContent('');
  };

  // ============ ALIASES LOGIC ============

  const loadAliases = async () => {
    setAliasesLoading(true);
    setAliasesError(null);
    try {
      const token = await getToken();
      const result = await getReviewQueue(token);
      setAliases(result.items || []);
    } catch (err) {
      console.error('Failed to load alias review queue:', err);
      setAliasesError('Failed to load aliases.');
    } finally {
      setAliasesLoading(false);
    }
  };

  const handleReviewAlias = async (reviewId, decision) => {
    setAliasesProcessingId(reviewId);
    try {
      const token = await getToken();
      await reviewEntityItem(reviewId, decision, token);
      setAliases(prev => prev.filter(item => item.id !== reviewId));
      onReviewChanged?.();
    } catch (err) {
      console.error('Failed to review alias:', err);
      setAliasesError('Failed to process review.');
    } finally {
      setAliasesProcessingId(null);
    }
  };

  // ============ RENDER ============

  if (!isOpen) return null;

  const totalCount = facts.length + aliases.length;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: ABLE_COLORS.bg,
          borderRadius: '24px',
          padding: '0',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Sparkles size={22} color={ABLE_COLORS.warm} />
            <h2 style={{ 
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              Review
            </h2>
            {totalCount > 0 && (
              <span style={{
                background: ABLE_COLORS.accent,
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                padding: '2px 8px',
                borderRadius: '10px'
              }}>
                {totalCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: ABLE_COLORS.textMuted,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs - only show if there are aliases */}
        {aliases.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '4px',
            padding: '12px 24px',
            background: ABLE_COLORS.neutral
          }}>
            <button
              onClick={() => setActiveTab('facts')}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: activeTab === 'facts' ? 'white' : 'transparent',
                color: activeTab === 'facts' ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: activeTab === 'facts' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Facts {!factsLoading && facts.length > 0 && `(${facts.length})`}
            </button>
            <button
              onClick={() => setActiveTab('aliases')}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: activeTab === 'aliases' ? 'white' : 'transparent',
                color: activeTab === 'aliases' ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: activeTab === 'aliases' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Aliases {!aliasesLoading && aliases.length > 0 && `(${aliases.length})`}
            </button>
          </div>
        )}

        {/* Content Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {activeTab === 'facts' ? (
            // ============ FACTS TAB ============
            <>
              {factsError && (
                <div style={{
                  padding: '12px 16px',
                  background: '#FEF2F2',
                  borderRadius: '12px',
                  color: '#DC2626',
                  marginBottom: '16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} />
                  {factsError}
                </div>
              )}

              {factsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px', color: ABLE_COLORS.primary }}>
                  <Loader size={28} className="spin" />
                </div>
              ) : facts.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <Sparkles size={36} color={ABLE_COLORS.warm} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, color: ABLE_COLORS.textSecondary, fontSize: '15px' }}>
                    Nothing to review right now
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: ABLE_COLORS.textMuted }}>
                    Chat with Mabel to help her learn about you
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {facts.map((fact) => (
                    <FactCard
                      key={fact.id}
                      fact={fact}
                      isProcessing={factsProcessingId === fact.id}
                      isEditing={editingFact?.id === fact.id}
                      editContent={editContent}
                      onEditContentChange={setEditContent}
                      onStartEdit={() => handleStartEditFact(fact)}
                      onSaveEdit={handleSaveEditFact}
                      onCancelEdit={handleCancelEditFact}
                      onConfirm={() => handleConfirmFact(fact.id)}
                      onReject={() => handleRejectFact(fact.id)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            // ============ ALIASES TAB ============
            <>
              {aliasesError && (
                <div style={{
                  padding: '12px 16px',
                  background: '#FEF2F2',
                  borderRadius: '12px',
                  color: '#DC2626',
                  marginBottom: '16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} />
                  {aliasesError}
                </div>
              )}

              {aliasesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px', color: ABLE_COLORS.primary }}>
                  <Loader size={28} className="spin" />
                </div>
              ) : aliases.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <Link2 size={36} color={ABLE_COLORS.primary} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, color: ABLE_COLORS.textSecondary, fontSize: '15px' }}>
                    No aliases to review
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {aliases.map((item) => (
                    <AliasCard
                      key={item.id}
                      item={item}
                      isProcessing={aliasesProcessingId === item.id}
                      onReview={handleReviewAlias}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {facts.length > 1 && activeTab === 'facts' && (
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${ABLE_COLORS.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
          }}>
            <button
              onClick={handleConfirmAllFacts}
              disabled={confirmingAll || editingFact}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: confirmingAll ? ABLE_COLORS.textMuted : ABLE_COLORS.primary,
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                cursor: confirmingAll || editingFact ? 'not-allowed' : 'pointer',
                color: 'white',
                fontWeight: '600'
              }}
            >
              {confirmingAll ? (
                <><Loader size={16} className="spin" /> Confirming...</>
              ) : (
                <><Check size={16} /> Confirm All</>
              )}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
